import { toast } from "sonner";
import { AgentInstructionAcks } from "../components/AgentInstructionAcks";
import { CnapsBadge, CnapsAgentCard } from "../components/CnapsBadge";
import { RgpdExportButton } from "../components/RgpdExportButton";
import { AgentPayrollFields, payrollBody } from "../components/AgentPayrollFields";
import { AgentDpae } from "../components/AgentDpae";
import { ContractHistory, RehireDialog } from "../components/AgentContracts";
import { PersonnelRegistryDialog } from "../components/PersonnelRegistry";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ImportButton } from "../components/ImportDialog";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Plus, Pencil, Trash2, X, ShieldCheck, Upload, FileText, ExternalLink, AlertTriangle, Camera, User, Search, FileSignature, Printer, Archive, ArchiveRestore, Ban, ShieldOff, CalendarDays, UserPlus, CalendarOff, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AgentLoginLink } from "../components/AgentLoginLink";
import { CredentialsDialog, PendingCredentials } from "../components/AgentCredentials";
import { GenerateDocDialog } from "./DocTemplates";
import { AgentReceiptsSection } from "../components/PlanningReceipts";
import { DIPLOMAS, ID_DOC_TYPES, CONTRACT_TYPES, contractTypeLabel, contractRequiresEnd, expiryStatus } from "../lib/diplomas";
import { DateInput } from "../components/DateInput";
import { AgentQuickContact } from "../components/AgentQuickContact";
import { BroadcastButton } from "../components/BroadcastMessage";
import { AgentEquipmentPanel } from "../components/equipment/Panels";
import { AgentAbsencesPanel } from "../components/absences/AgentAbsencesPanel";
import { AbsencesCalendar } from "../components/absences/AbsencesCalendar";

const SSIAP = ["SSIAP 1", "SSIAP 2", "SSIAP 3"];

// Build absolute URL for an uploaded file (used for <img src>).
const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}/download`;

// Fetch a file blob with auth and return an objectURL (cookie auth doesn't reach <img> in iframes).
async function fetchAuthenticatedImageUrl(id) {
  const res = await http.get(`/files/${id}/download`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

function pickWorstExpiry(agent) {
  // Returns the most urgent expiry status across diplomas + ID doc + CDD end.
  let worst = { status: "ok", days: Infinity, label: null };
  const items = [];
  if (agent.id_doc_expires_on) items.push({ label: "Pièce d'identité", date: agent.id_doc_expires_on });
  if (agent.diplomas) {
    for (const d of DIPLOMAS) {
      const expires = agent.diplomas[d.code]?.expires_on;
      if (expires) items.push({ label: d.label, date: expires });
    }
  }
  if (agent.contract_type && agent.contract_end_date && contractRequiresEnd(agent.contract_type)) {
    items.push({ label: `Fin de ${contractTypeLabel(agent.contract_type)}`, date: agent.contract_end_date });
  }
  for (const it of items) {
    const s = expiryStatus(it.date);
    if (s.status === "expired" && worst.status !== "expired") {
      worst = { status: "expired", days: s.days, label: it.label };
    } else if (s.status === "warning" && worst.status === "ok") {
      worst = { status: "warning", days: s.days, label: it.label };
    } else if (s.status === "warning" && worst.status === "warning" && s.days < worst.days) {
      worst = { status: "warning", days: s.days, label: it.label };
    } else if (s.status === "expired" && worst.status === "expired" && s.days < worst.days) {
      worst = { status: "expired", days: s.days, label: it.label };
    }
  }
  return worst;
}

function ExpiryBadge({ agent }) {
  const w = pickWorstExpiry(agent);
  if (w.status === "ok") return null;
  const cls = w.status === "expired"
    ? "bg-rose-100 text-rose-800 border-rose-300"
    : "bg-amber-100 text-amber-800 border-amber-300";
  const text = w.status === "expired"
    ? `${w.label} expiré${w.days < -1 ? `e (${Math.abs(w.days)}j)` : "e"}`
    : `${w.label} expire ${w.days <= 0 ? "aujourd'hui" : `dans ${w.days}j`}`;
  return (
    <span title={text} className={`inline-flex items-center gap-1 ${cls} border rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest`}>
      <AlertTriangle className="h-3 w-3" /> {w.status === "expired" ? "Expiré" : `<${w.days <= 0 ? 1 : w.days}j`}
    </span>
  );
}

function AgentAvatar({ agent, size = 36 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    if (agent?.photo_file_id) {
      fetchAuthenticatedImageUrl(agent.photo_file_id)
        .then((u) => { setSrc(u); revoke = u; })
        .catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [agent?.photo_file_id]);

  if (src) {
    return <img src={src} alt={agent?.first_name} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  const initials = `${agent?.first_name?.[0] || ""}${agent?.last_name?.[0] || ""}`.toUpperCase() || "?";
  return (
    <div className="rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-white grid place-items-center font-bold" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
}

export default function Agents() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rehire, setRehire] = useState(null);
  const [registry, setRegistry] = useState(false);
  const [absCal, setAbsCal] = useState(false);
  const [docFor, setDocFor] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("active");
  const [blFor, setBlFor] = useState(null);
  const [cred, setCred] = useState(null);
  const { user, refresh } = useAuth();
  const { lastEvent } = useRealtime();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => { if (user?.agent_limit) refresh(); }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deleted, setDeleted] = useState([]);
  const openAgentPlanning = async (a) => {
    const m = window.prompt(`Planning de ${a.first_name} ${a.last_name} — mois (AAAA-MM) :`, new Date().toISOString().slice(0, 7));
    if (!m) return;
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(m)) { alert("Mois invalide : utilisez le format AAAA-MM (ex. 2026-11)."); return; }
    const w = window.open("", "_blank");
    try {
      const r = await http.get(`/agents/${a.id}/planning.pdf`, { params: { month: m }, responseType: "blob" });
      w.location.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    } catch (e) { w?.close(); alert(formatApiError(e)); }
  };
  const load = async () => {
    const [r, d] = await Promise.all([http.get("/agents"), http.get("/agents/deleted")]);
    setItems(r.data.filter((a) => !a.subcontractor_id)); setDeleted(d.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "agent_changed") load(); }, [lastEvent]);

  // Ouvre automatiquement la fiche agent si ?edit=<id> est présent dans l'URL
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && items.length > 0) {
      const target = items.find((a) => a.id === editId);
      if (target) {
        setEditing(target);
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [items, searchParams, setSearchParams]);

  const viewOf = (a) => (a.blacklisted ? "blacklisted" : a.archived ? "archived" : "active");
  const archivedCount = items.filter((a) => viewOf(a) === "archived").length;
  const blacklistedCount = items.filter((a) => viewOf(a) === "blacklisted").length;
  const activeCount = items.length - archivedCount - blacklistedCount;
  const unblacklist = async (a) => {
    const reason = window.prompt(`Retirer ${a.first_name} ${a.last_name} de la blacklist ?\n\nIl pourra de nouveau être recruté. Sa fiche sera remise ${a.archived ? "dans les archivés" : "dans les agents actifs"}.\n\nMotif du retrait (obligatoire) :`);
    if (reason === null) return;
    if (!reason.trim()) { window.alert("Le motif du retrait est obligatoire."); return; }
    try { await http.post(`/agents/${a.id}/unblacklist`, { reason }); load(); } catch (e) { window.alert(formatApiError(e)); }
  };
  const archive = async (a) => {
    if (!window.confirm(`Archiver ${a.first_name} ${a.last_name} ?\n\nIl n'apparaîtra plus dans la liste, ne pourra plus se connecter à l'application mobile et ses vacations futures seront libérées (à réattribuer).`)) return;
    const r = await http.post(`/agents/${a.id}/archive`);
    load();
    if (r.data.freed_vacations) window.alert(`${r.data.freed_vacations} vacation(s) future(s) libérée(s) dans le planning.`);
  };
  const restore = async (a) => { await http.post(`/agents/${a.id}/restore`); load(); };

  const filtered = items.filter((a) => {
    if (viewOf(a) !== view) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [
      a.first_name, a.last_name, a.email, a.phone, a.carte_pro_number, a.matricule,
      a.social_security_number, a.id_doc_number,
    ].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="agents-page">
      {user?.role === "admin" && <AgentLoginLink slug={user?.company?.slug} />}
      {(user?.role === "admin" || Array.isArray(user?.site_ids)) && <PendingCredentials onSend={(ids) => setCred({ scope: "agent", agent_ids: ids })} />}
      {cred && <CredentialsDialog initial={cred} onClose={() => setCred(null)} />}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Agents</h1>
          <p className="text-sm text-slate-600">{activeCount} agents actifs{archivedCount ? ` · ${archivedCount} archivé${archivedCount > 1 ? "s" : ""}` : ""}{blacklistedCount ? ` · ${blacklistedCount} blacklisté${blacklistedCount > 1 ? "s" : ""}` : ""}{search.trim() ? ` · ${filtered.length} affiché${filtered.length > 1 ? "s" : ""}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="agents-search"
              placeholder="Rechercher un agent (nom, email, carte pro…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm w-80 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
            />
          </div>
          <BroadcastButton className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md text-sm font-semibold" />
          {(user?.role === "admin" || Array.isArray(user?.site_ids)) && <button data-testid="open-credentials" onClick={() => setCred({})} className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md text-sm font-semibold"><KeyRound className="h-4 w-4" /> Identifiants</button>}
          <ImportButton kind="agents" onDone={load} />
          <button data-testid="open-absences-calendar" onClick={() => setAbsCal(true)} className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md text-sm font-semibold"><CalendarOff className="h-4 w-4" /> Absences</button>
          <button data-testid="open-personnel-registry" onClick={() => setRegistry(true)} className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md text-sm font-semibold"><FileText className="h-4 w-4" /> Registre du personnel</button>
          <button data-testid="add-agent" disabled={!!user?.agent_limit && (user?.agents_active ?? 0) >= user.agent_limit} title={user?.agent_limit && (user?.agents_active ?? 0) >= user.agent_limit ? "Limite de votre formule atteinte" : undefined} onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-semibold"><Plus className="h-4 w-4" /> Nouvel agent</button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="agents-view-tabs">
        {[["active", "Agents actifs", activeCount], ["archived", "Archivés", archivedCount], ["blacklisted", "Blacklistés", blacklistedCount], ["deleted", "Supprimés", deleted.length]].map(([k, l, n]) => (
          <button key={k} data-testid={`agents-tab-${k}`} onClick={() => setView(k)}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${view === k ? (k === "blacklisted" ? "border-rose-600 text-rose-700" : "border-sky-600 text-sky-700") : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {k === "archived" && <Archive className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}{k === "blacklisted" && <Ban className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}{k === "deleted" && <Trash2 className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}{l} <span className="ml-1 text-[11px] rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">{n}</span>
          </button>
        ))}
      </div>

      {view === "deleted" ? <DeletedAgents items={deleted} search={search} isAdmin={user?.role === "admin"} onChanged={load} /> : (
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <th className="px-4 py-3">Agent</th>
              <th className="px-4 py-3">Contrat</th>
              <th className="px-4 py-3">Téléphone</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Diplômes</th>
              <th className="px-4 py-3">Carte pro</th>
              <th className="px-4 py-3">Alertes</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((a) => {
              const validDiplomas = DIPLOMAS.filter((d) => a.diplomas?.[d.code]?.number || a.diplomas?.[d.code]?.expires_on);
              return (
                <tr
                  key={a.id}
                  onClick={() => setEditing(a)}
                  data-testid={`agent-row-${a.id}`}
                  className={`hover:bg-sky-50/60 cursor-pointer transition-colors ${a.archived && !a.blacklisted ? "opacity-70" : ""} ${a.blacklisted ? "bg-rose-50/40" : ""}`}
                  title="Cliquer pour modifier"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <AgentAvatar agent={a} size={36} />
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{a.last_name} <span className="text-slate-500 font-normal">{a.first_name}</span></div>
                        {a.staff_user_id && <div data-testid={`agent-staff-badge-${a.id}`} className="text-[10px] font-bold uppercase tracking-widest text-emerald-700" title="Profil utilisateur : planifiable, sans espace agent">Utilisateur · sans espace agent</div>}
                        {a.matricule && <div className="text-[10px] font-mono text-slate-500" data-testid={`agent-matricule-${a.id}`}>Matricule {a.matricule}</div>}
                        {a.ssiap_level && <div className="text-[10px] text-slate-500 uppercase tracking-widest">{a.ssiap_level}</div>}
                        {a.blacklisted && <div data-testid={`agent-bl-reason-${a.id}`} className="text-[11px] text-rose-700 font-semibold max-w-[260px] truncate" title={a.blacklist_reason}><Ban className="h-3 w-3 inline -mt-0.5" /> {a.blacklist_reason}{a.blacklisted_at ? ` · ${new Date(a.blacklisted_at).toLocaleDateString("fr-FR")}` : ""}</div>}
                        {!a.blacklisted && a.archived && a.archived_at && <div className="text-[10px] text-slate-500">Archivé le {new Date(a.archived_at).toLocaleDateString("fr-FR")}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><ContractCell agent={a} /></td>
                  <td className="px-4 py-3">{a.phone || "—"}</td>
                  <td className="px-4 py-3">{a.email || "—"}</td>
                  <td className="px-4 py-3">
                    {validDiplomas.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {validDiplomas.slice(0, 3).map((d) => (
                          <span key={d.code} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                            <ShieldCheck className="h-2.5 w-2.5" />{d.label}
                          </span>
                        ))}
                        {validDiplomas.length > 3 && <span className="text-[10px] text-slate-500">+{validDiplomas.length - 3}</span>}
                      </div>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{a.carte_pro_number || "—"}{a.cnaps && <div className="mt-1"><CnapsBadge status={a.cnaps.status} validUntil={a.cnaps.valid_until} testid={`agent-cnaps-${a.id}`} /></div>}</td>
                  <td className="px-4 py-3"><ExpiryBadge agent={a} /></td>
                  <td className="px-4 py-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`docs-agent-${a.id}`} onClick={(e)=>{ e.stopPropagation(); setDocFor(a); }} className="p-1.5 rounded hover:bg-amber-50" title="Générer un document"><FileSignature className="h-4 w-4 text-amber-600" /></button>
                    <button data-testid={`edit-agent-${a.id}`} onClick={() => setEditing(a)} className="p-1.5 rounded hover:bg-slate-100" title="Modifier"><Pencil className="h-4 w-4 text-slate-600" /></button>
                    {!a.archived && !a.blacklisted && (user?.role === "admin" || Array.isArray(user?.site_ids)) && <button data-testid={`send-cred-${a.id}`} onClick={() => setCred({ scope: "agent", agent_ids: [a.id] })} className="p-1.5 rounded hover:bg-amber-50" title="Envoyer les identifiants (nouveau PIN)"><KeyRound className="h-4 w-4 text-[#C9962E]" /></button>}
                    {a.blacklisted
                      ? (user?.role === "admin" && <button data-testid={`unblacklist-agent-${a.id}`} onClick={() => unblacklist(a)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-emerald-700 hover:bg-emerald-50" title="Retirer de la blacklist"><ShieldOff className="h-4 w-4" /> Retirer</button>)
                      : <button data-testid={`blacklist-agent-${a.id}`} onClick={() => setBlFor(a)} className="p-1.5 rounded hover:bg-rose-50" title="Blacklister"><Ban className="h-4 w-4 text-rose-600" /></button>}
                    {!a.blacklisted && a.archived && <button data-testid={`rehire-agent-${a.id}`} onClick={() => setRehire(a)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-sky-700 hover:bg-sky-50" title="Réintégrer (nouveau contrat)"><UserPlus className="h-4 w-4" /> Réintégrer</button>}
                    {a.blacklisted || (a.archived && a.contract_closed) ? null : a.archived
                      ? <button data-testid={`restore-agent-${a.id}`} onClick={() => restore(a)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-emerald-700 hover:bg-emerald-50" title="Restaurer"><ArchiveRestore className="h-4 w-4" /> Restaurer</button>
                      : <button data-testid={`archive-agent-${a.id}`} onClick={() => archive(a)} className="p-1.5 rounded hover:bg-slate-100" title="Archiver"><Archive className="h-4 w-4 text-slate-600" /></button>}
                    <button data-testid={`agent-planning-pdf-${a.id}`} onClick={() => openAgentPlanning(a)} className="p-1.5 rounded hover:bg-sky-50" title="Planning du mois (PDF)"><CalendarDays className="h-4 w-4 text-sky-700" /></button>
                    <button data-testid={`delete-agent-${a.id}`} onClick={async () => { if (askDelete(`Supprimer ${a.first_name} ${a.last_name} ?\n\nCette personne ne pourra plus postuler sur /postuler (un administrateur pourra l'autoriser de nouveau depuis l'onglet « Supprimés »).`)) { await http.delete(`/agents/${a.id}`); load(); } }} className="p-1.5 rounded hover:bg-rose-50" title={delTitle()}><Trash2 className="h-4 w-4 text-rose-600" /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-10 text-sm">{view === "blacklisted" && !search.trim() ? "Aucun agent blacklisté." : view === "archived" && !search.trim() ? "Aucun agent archivé." : items.length === 0 ? "Aucun agent. Cliquez sur \"Nouvel agent\" pour commencer." : "Aucun agent ne correspond à la recherche."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {editing && <AgentModal value={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {registry && <PersonnelRegistryDialog onClose={() => setRegistry(false)} />}
      {absCal && <AbsencesCalendar onClose={() => setAbsCal(false)} />}
      {rehire && <RehireDialog agent={rehire} onClose={() => setRehire(null)} onDone={load} />}
      {docFor && <GenerateDocDialog agent={docFor} onClose={()=>setDocFor(null)} />}
      {blFor && <BlacklistDialog agent={blFor} onClose={() => setBlFor(null)} onDone={() => { setBlFor(null); load(); }} />}
    </div>
  );
}

function AgentModal({ value, onClose, onSaved }) {
  const initialDiplomas = () => {
    const out = {};
    for (const d of DIPLOMAS) {
      out[d.code] = { number: value?.diplomas?.[d.code]?.number ?? "", expires_on: value?.diplomas?.[d.code]?.expires_on ?? "" };
    }
    return out;
  };
  const [v, setV] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    address: "", postal_code: "", city: "",
    ssiap_level: "", carte_pro_number: "", notes: "", pin_code: "", matricule: "", alarm_code: "",
    photo_file_id: null,
    social_security_number: "",
    id_doc_type: "", id_doc_number: "", id_doc_expires_on: "",
    contract_type: "", contract_start_date: "", contract_end_date: "",
    ...value,
  });
  const [diplomas, setDiplomas] = useState(initialDiplomas);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  const loadFiles = async () => {
    if (!v.id) return;
    setLoadingFiles(true);
    try {
      const r = await http.get(`/files?kind=agent_doc&ref_id=${v.id}`);
      setFiles(r.data);
    } finally { setLoadingFiles(false); }
  };
  useEffect(() => { loadFiles(); /* eslint-disable-next-line */ }, [v.id]);

  const setDiploma = (code, key, val) =>
    setDiplomas((prev) => ({ ...prev, [code]: { ...prev[code], [key]: val } }));

  const serializeDiplomas = () => {
    const out = {};
    let hasAny = false;
    for (const d of DIPLOMAS) {
      const cell = diplomas[d.code] || {};
      if (cell.number || cell.expires_on) {
        out[d.code] = {
          number: cell.number || "",
          expires_on: cell.expires_on || null,
        };
        hasAny = true;
      }
    }
    return hasAny ? out : null;
  };

  const [printing, setPrinting] = useState(false);

  const buildBody = () => ({
        first_name: v.first_name, last_name: v.last_name, phone: v.phone, email: v.email,
        birth_date: v.birth_date || null,
        address: v.address || "", postal_code: v.postal_code || "", city: v.city || "",
        ssiap_level: v.ssiap_level, carte_pro_number: v.carte_pro_number, notes: v.notes,
        matricule: (v.matricule || "").trim(), alarm_code: (v.alarm_code || "").trim(),
        pin_code: v.pin_code || "", pin_disable: !!v.pin_disable,
        photo_file_id: v.photo_file_id || null,
        social_security_number: v.social_security_number || "",
        id_doc_type: v.id_doc_type || "",
        id_doc_number: v.id_doc_number || "",
        id_doc_expires_on: v.id_doc_expires_on || null,
        diplomas: serializeDiplomas(),
        contract_type: v.contract_type || "",
        contract_start_date: v.contract_start_date || null,
        contract_end_date: contractRequiresEnd(v.contract_type) ? (v.contract_end_date || null) : null,
        ...payrollBody(v),
  });

  // Simple avertissement si la personne correspond à un agent blacklisté (création uniquement)
  const confirmNotBlacklisted = async () => {
    if (v.id) return true;
    const { data } = await http.post("/agents/check-blacklist", buildBody());
    if (!data.match) return true;
    return window.confirm(`ATTENTION : cette personne correspond à un agent BLACKLISTÉ.\n\n${data.match.name}\nMotif : ${data.match.reason}\nCorrespondance : ${data.match.matched_on.join(", ")}\n\nCréer quand même la fiche ?`);
  };

  const printFiche = async () => {
    setErr("");
    if (!(await confirmNotBlacklisted())) return;
    const win = window.open("", "_blank");
    setPrinting(true);
    try {
      let id = v.id;
      if (id) await http.put(`/agents/${id}`, buildBody());
      else {
        const r = await http.post("/agents", buildBody());
        id = r.data.id;
        setV((prev) => ({ ...prev, id }));
      }
      onSaved();
      const res = await http.get(`/agents/${id}/fiche.pdf?inline=true`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url;
      else Object.assign(document.createElement("a"), { href: url, download: `Fiche_${v.last_name}_${v.first_name}.pdf` }).click();
    } catch (e) {
      if (win) win.close();
      setErr(formatApiError(e));
    } finally { setPrinting(false); }
  };

  const saveDraft = async () => {
    setErr("");
    try { await http.put(`/agents/${v.id}`, buildBody()); onSaved(); return true; }
    catch (e) { setErr(formatApiError(e)); toast.error(formatApiError(e)); return false; }
  };

  const save = async () => {
    setErr("");
    try {
      if (!(await confirmNotBlacklisted())) return;
      const body = buildBody();
      if (v.id) await http.put(`/agents/${v.id}`, body);
      else {
        const r = await http.post("/agents", body);
        setV({ ...v, id: r.data.id });
        onSaved();
        return; // keep modal open to allow uploading docs/photo
      }
      onSaved(); onClose();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", "agent_doc");
      fd.append("ref_id", v.id);
      fd.append("file", file);
      await http.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      loadFiles();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setErr("La photo doit être une image (JPG, PNG, WEBP).");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("kind", "agent_photo");
      fd.append("ref_id", v.id || "new");
      fd.append("file", file);
      const r = await http.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setV({ ...v, photo_file_id: r.data.id });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploadingPhoto(false); }
  };

  const removeFile = async (id) => {
    await http.delete(`/files/${id}`);
    loadFiles();
  };

  const photoStatus = expiryStatus(v.id_doc_expires_on);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[95vh] overflow-auto border border-slate-200 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="font-display text-xl font-bold shrink-0">{v.id ? "Modifier" : "Nouvel"} agent</h3>
            <AgentQuickContact agent={value?.id ? { ...value, phone: v.phone } : null} />
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-5">

          {/* Photo + identité courte */}
          <div className="flex gap-4 items-start">
            <PhotoUploader
              fileId={v.photo_file_id}
              onPick={() => photoRef.current?.click()}
              onClear={() => setV({ ...v, photo_file_id: null })}
              uploading={uploadingPhoto}
            />
            <input type="file" ref={photoRef} accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 min-w-0">
              <L label="Prénom *"><input data-testid="agent-first-name" className="input" value={v.first_name} onChange={(e) => setV({ ...v, first_name: e.target.value })} /></L>
              <L label="Nom *"><input data-testid="agent-last-name" className="input" value={v.last_name} onChange={(e) => setV({ ...v, last_name: e.target.value })} /></L>
              <L label="Téléphone"><input className="input" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></L>
              <L label="Email"><input className="input" type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></L>
              <L label="Date de naissance"><DateInput data-testid="agent-birth-date" className="input" value={v.birth_date || ""} onChange={(e) => setV({ ...v, birth_date: e.target.value })} /></L>
            </div>
          </div>

          <Section title="Adresse postale">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <L label="Adresse"><input data-testid="agent-address" className="input" value={v.address || ""} placeholder="N° et rue" onChange={(e) => setV({ ...v, address: e.target.value })} /></L>
              </div>
              <L label="Code postal"><input data-testid="agent-postal-code" className="input" value={v.postal_code || ""} onChange={(e) => setV({ ...v, postal_code: e.target.value })} /></L>
              <div className="md:col-span-2">
                <L label="Ville"><input data-testid="agent-city" className="input" value={v.city || ""} onChange={(e) => setV({ ...v, city: e.target.value })} /></L>
              </div>
            </div>
          </Section>

          <Section title="Informations administratives">
            <div className="grid grid-cols-2 gap-3">
              <L label="Matricule">
                <input data-testid="agent-matricule" className="input font-mono" value={v.matricule || ""} placeholder={v.id ? "" : "Attribué automatiquement si vide"} onChange={(e) => setV({ ...v, matricule: e.target.value })} />
              </L>
              <L label="Code télésurveillance (001 à 999)">
                <input data-testid="agent-alarm-code" className="input font-mono" inputMode="numeric" maxLength={3} value={v.alarm_code || ""} placeholder="Fourni par votre centre" onChange={(e) => setV({ ...v, alarm_code: e.target.value.replace(/[^0-9]/g, "") })} />
              </L>
              <L label="N° Sécurité sociale">
                <input
                  data-testid="agent-ssn"
                  className="input font-mono"
                  inputMode="numeric"
                  maxLength={15}
                  placeholder="1 95 04 75 116 037 12"
                  value={v.social_security_number}
                  onChange={(e) => setV({ ...v, social_security_number: e.target.value })}
                />
              </L>
              <L label="N° Carte pro CNAPS">
                <input className="input font-mono" value={v.carte_pro_number} onChange={(e) => setV({ ...v, carte_pro_number: e.target.value })} />
              </L>
              {v.id && <div className="col-span-full"><CnapsAgentCard agent={v} onChecked={loadFiles} /></div>}
              <L label="SSIAP (niveau actuel)">
                <select className="input" value={v.ssiap_level || ""} onChange={(e) => setV({ ...v, ssiap_level: e.target.value })}>
                  <option value="">— Aucun —</option>
                  {SSIAP.map((x) => <option key={x}>{x}</option>)}
                </select>
              </L>
              <L label={v.pin_set ? "Code PIN app mobile (défini — saisir 6 chiffres pour le changer)" : "Code PIN app mobile (6 chiffres)"}>
                <input data-testid="agent-pin" type="password" autoComplete="new-password" inputMode="numeric" maxLength={6} pattern="[0-9]*" className="input" value={v.pin_code || ""} onChange={(e) => setV({ ...v, pin_code: e.target.value.replace(/[^0-9]/g, ""), pin_disable: false })} placeholder={v.pin_set ? "•••••• (inchangé)" : "(vide = pas d'accès)"} />
                {v.pin_set && <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-600"><input data-testid="agent-pin-disable" type="checkbox" checked={!!v.pin_disable} onChange={(e) => setV({ ...v, pin_disable: e.target.checked, pin_code: "" })} />Désactiver l&apos;accès à l&apos;application</label>}
              </L>
            </div>
          </Section>

          <Section title="Pièce d'identité">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <L label="Type">
                <select data-testid="agent-id-doc-type" className="input" value={v.id_doc_type} onChange={(e) => setV({ ...v, id_doc_type: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {ID_DOC_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </L>
              <L label="Numéro">
                <input data-testid="agent-id-doc-number" className="input font-mono" value={v.id_doc_number} onChange={(e) => setV({ ...v, id_doc_number: e.target.value })} />
              </L>
              <L label="Date d'expiration">
                <div className="flex items-center gap-2">
                  <DateInput data-testid="agent-id-doc-expires" className="input" value={v.id_doc_expires_on || ""} onChange={(e) => setV({ ...v, id_doc_expires_on: e.target.value })} />
                </div>
                <ExpiryHint status={photoStatus} />
              </L>
            </div>
          </Section>

          <Section title="Contrat de travail" subtitle="Type, date d'embauche, et fin de contrat (CDD, intérim, stage, apprentissage). Une alerte s'affichera <30j avant la fin.">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <L label="Type de contrat">
                <select data-testid="agent-contract-type" className="input" value={v.contract_type} onChange={(e) => setV({ ...v, contract_type: e.target.value })}>
                  <option value="">— Non précisé —</option>
                  {CONTRACT_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </L>
              <L label="Date de début">
                <DateInput data-testid="agent-contract-start" className="input" value={v.contract_start_date || ""} onChange={(e) => setV({ ...v, contract_start_date: e.target.value })} />
              </L>
              <L label={contractRequiresEnd(v.contract_type) ? "Date de fin *" : "Date de fin"}>
                <DateInput
                  data-testid="agent-contract-end"
                  disabled={!contractRequiresEnd(v.contract_type)}
                  className="input disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                  value={v.contract_end_date || ""}
                  onChange={(e) => setV({ ...v, contract_end_date: e.target.value })}
                />
                <ExpiryHint status={contractRequiresEnd(v.contract_type) ? expiryStatus(v.contract_end_date) : { status: "none" }} />
              </L>
            </div>
          </Section>

          <Section title="Prépaie" subtitle="Informations reprises dans la rubrique Prépaie et l'export Excel. La qualification détermine le taux horaire brut (Paramètres > Paie).">
            <AgentPayrollFields v={v} setV={setV} />
          </Section>

          <Section title="DPAE & contrats" subtitle="Déclaration préalable à l'embauche (URSSAF), clôture du contrat et historique des contrats successifs.">
            <AgentDpae v={v} setV={setV} onUploaded={loadFiles} beforeOpen={saveDraft} />
            <div className="mt-4"><ContractHistory agent={v} beforeClose={saveDraft} onClosed={() => { onSaved(); onClose(); }} /></div>
          </Section>

          <Section title="Diplômes & habilitations" subtitle="Saisissez le numéro et la date de fin de validité pour chaque diplôme détenu.">
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
                    <th className="text-left py-2 pl-1">Diplôme</th>
                    <th className="text-left py-2">Numéro</th>
                    <th className="text-left py-2">Validité</th>
                    <th className="text-left py-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {DIPLOMAS.map((d) => {
                    const cell = diplomas[d.code] || {};
                    const stat = expiryStatus(cell.expires_on);
                    return (
                      <tr key={d.code} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-1.5 pl-1 font-semibold text-slate-700 whitespace-nowrap">{d.label}</td>
                        <td className="py-1.5 pr-2">
                          <input
                            data-testid={`diploma-${d.code}-number`}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white"
                            placeholder="N° de diplôme"
                            value={cell.number}
                            onChange={(e) => setDiploma(d.code, "number", e.target.value)}
                          />
                        </td>
                        <td className="py-1.5 pr-2 w-44">
                          <DateInput
                            data-testid={`diploma-${d.code}-expires`}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white"
                            value={cell.expires_on || ""}
                            onChange={(e) => setDiploma(d.code, "expires_on", e.target.value)}
                          />
                        </td>
                        <td className="py-1.5 w-32">
                          <ExpiryHint status={stat} compact />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          <BlacklistHistory agent={value} />

          {value?.id && <AgentReceiptsSection agentId={value.id} />}
          {value?.id && <AgentInstructionAcks agentId={value.id} />}

          <Section title="Notes">
            <textarea className="input" rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
          </Section>

          {v.id && <Section title="Absences et congés" subtitle="Congés, maladie, indisponibilités… Les absences validées bloquent le planning et sont reprises automatiquement en Prépaie."><AgentAbsencesPanel agentId={v.id} /></Section>}
          {v.id && <Section title="Équipements"><AgentEquipmentPanel agentId={v.id} /></Section>}

          {v.id && (
            <Section title={`Documents de l'agent (PDF/image · max 10 Mo)`}>
              <div className="flex justify-end mb-2">
                <input type="file" ref={fileRef} accept=".pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                <button data-testid="agent-upload-doc" type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"><Upload className="h-3.5 w-3.5" />{uploading ? "Upload…" : "Téléverser un document"}</button>
              </div>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                {loadingFiles && <div className="px-3 py-4 text-xs text-slate-500 text-center">Chargement…</div>}
                {!loadingFiles && files.length === 0 && <div className="px-3 py-4 text-xs text-slate-500 text-center">Aucun document téléversé.</div>}
                {files.map((f) => (
                  <div key={f.id} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm truncate">{f.original_filename}</span>
                      <span className="text-[10px] text-slate-400">{Math.ceil((f.size || 0) / 1024)} Ko</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-100"><ExternalLink className="h-3.5 w-3.5 text-slate-600" /></a>
                      <button onClick={() => removeFile(f.id)} className="p-1.5 rounded hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {err && <div data-testid="agent-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>

        <div data-testid="agent-modal-footer" className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <div className="flex gap-2 w-full sm:w-auto sm:mr-auto [&>*]:flex-1 sm:[&>*]:flex-none">
            <button data-testid="agent-print-fiche" onClick={printFiche} disabled={printing} className="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100 disabled:opacity-60 whitespace-nowrap">
              <Printer className="h-4 w-4 shrink-0" /> {printing ? "Génération…" : <><span className="sm:hidden">Fiche PDF</span><span className="hidden sm:inline">Imprimer la fiche PDF</span></>}
            </button>
            {v.id && <span className="flex empty:hidden [&>*]:w-full [&>*]:justify-center [&>*]:py-2 [&>*]:whitespace-nowrap"><RgpdExportButton url={`/agents/${v.id}/rgpd-export.pdf`} testid="agent-rgpd-export" /></span>}
          </div>
          <div className="flex gap-2 w-full sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none">
            <button onClick={onClose} data-testid="agent-close" className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Fermer</button>
            <button data-testid="agent-save" onClick={save} className="px-4 py-2 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-md">{v.id ? "Enregistrer" : "Créer"}</button>
          </div>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;border-radius:6px;padding:.55rem .7rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #0ea5e933}`}</style>
    </div>
  );
}

function PhotoUploader({ fileId, onPick, onClear, uploading }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    if (fileId) {
      fetchAuthenticatedImageUrl(fileId)
        .then((u) => { setSrc(u); revoke = u; })
        .catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="h-28 w-28 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden relative">
        {src ? (
          <img src={src} alt="agent" className="w-full h-full object-cover" data-testid="agent-photo-preview" />
        ) : (
          <User className="h-10 w-10 text-slate-300" />
        )}
        {uploading && <div className="absolute inset-0 bg-white/70 grid place-items-center text-xs font-bold text-slate-700">…</div>}
      </div>
      <div className="flex gap-1.5">
        <button type="button" data-testid="agent-photo-pick" onClick={onPick} className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-100">
          <Camera className="h-3 w-3" /> {fileId ? "Changer" : "Photo"}
        </button>
        {fileId && (
          <button type="button" onClick={onClear} className="inline-flex items-center gap-1 text-xs font-semibold border border-rose-200 text-rose-700 rounded px-2 py-1 hover:bg-rose-50">
            Retirer
          </button>
        )}
      </div>
    </div>
  );
}

function ExpiryHint({ status, compact }) {
  if (!status || status.status === "none") {
    return compact ? <span className="text-[10px] text-slate-400">—</span> : null;
  }
  const map = {
    ok: { c: "text-emerald-700 bg-emerald-50 border-emerald-200", t: `Valide${status.days != null ? ` (${status.days}j)` : ""}` },
    warning: { c: "text-amber-800 bg-amber-50 border-amber-300", t: `Expire dans ${status.days}j` },
    expired: { c: "text-rose-700 bg-rose-50 border-rose-300", t: `Expiré (${Math.abs(status.days)}j)` },
  };
  const m = map[status.status];
  return <span className={`inline-block ${m.c} border rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest mt-1`}>{m.t}</span>;
}

function Section({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-4 bg-slate-50/40">
      <div className="mb-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>;
}

function ContractCell({ agent }) {
  const type = agent.contract_type;
  if (!type) return <span className="text-slate-400 text-xs">—</span>;
  const requiresEnd = contractRequiresEnd(type);
  const endDate = agent.contract_end_date;
  const stat = requiresEnd ? expiryStatus(endDate) : null;
  const colorByType = {
    CDI: "bg-emerald-50 text-emerald-700 border-emerald-200",
    CDD: "bg-amber-50 text-amber-800 border-amber-200",
    interim: "bg-purple-50 text-purple-700 border-purple-200",
    stage: "bg-sky-50 text-sky-700 border-sky-200",
    apprentissage: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  const cls = colorByType[type] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-block w-fit ${cls} border rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest`}>
        {contractTypeLabel(type)}
      </span>
      {requiresEnd && endDate && (
        <span className={`text-[10px] ${stat?.status === "expired" ? "text-rose-700 font-bold" : stat?.status === "warning" ? "text-amber-700 font-bold" : "text-slate-500"}`}>
          {stat?.status === "expired" ? `Fin: ${endDate} (échue)` : stat?.status === "warning" ? `Fin dans ${stat.days}j` : `Fin: ${endDate}`}
        </span>
      )}
    </div>
  );
}

function BlacklistDialog({ agent, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!reason.trim()) return setErr("Le motif est obligatoire.");
    setBusy(true); setErr("");
    try {
      const r = await http.post(`/agents/${agent.id}/blacklist`, { reason });
      if (r.data.freed_vacations) window.alert(`${r.data.freed_vacations} vacation(s) future(s) libérée(s) dans le planning.`);
      onDone();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-md border border-rose-200 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="blacklist-dialog">
        <div className="px-5 py-4 bg-rose-50 border-b border-rose-200 flex items-center gap-2">
          <Ban className="h-5 w-5 text-rose-600" />
          <h3 className="font-display text-lg font-bold text-rose-900">Blacklister {agent.first_name} {agent.last_name}</h3>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <p className="text-slate-600">Cette personne ne pourra plus être recrutée : toute nouvelle candidature correspondante (email, téléphone, n° SS, carte pro ou nom + date de naissance) sera signalée et bloquée. Son accès mobile est coupé et ses vacations futures sont libérées.</p>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Motif *</div>
            <textarea data-testid="blacklist-reason" rows={3} className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : abandon de poste, faute grave…" />
          </label>
          {err && <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-100">Annuler</button>
          <button data-testid="blacklist-submit" onClick={submit} disabled={busy} className="px-4 py-2 text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-md disabled:opacity-60">{busy ? "…" : "Blacklister"}</button>
        </div>
      </div>
    </div>
  );
}

function BlacklistHistory({ agent }) {
  const hist = [...(agent?.blacklist_history || [])];
  if (!hist.length && agent?.blacklisted) hist.push({ action: "blacklist", reason: agent.blacklist_reason, by: agent.blacklisted_by, at: agent.blacklisted_at });
  if (!hist.length) return null;
  return (
    <Section title="Historique blacklist" subtitle="Qui a blacklisté ou retiré cet agent, quand et pourquoi.">
      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3" data-testid="blacklist-history">
        {hist.slice().reverse().map((h, i) => {
          const bl = h.action === "blacklist";
          return (
            <li key={i} className="ml-4" data-testid={`blacklist-history-item-${i}`}>
              <span className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full ring-2 ring-white ${bl ? "bg-rose-600" : "bg-emerald-600"}`} />
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${bl ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>{bl ? "Blacklisté" : "Retiré de la blacklist"}</span>
                <span className="text-slate-500">{h.at ? new Date(h.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                <span className="text-slate-500">par <b className="text-slate-700">{h.by || "—"}</b></span>
              </div>
              <div className="text-sm text-slate-800 mt-0.5">{h.reason || <i className="text-slate-400">Aucun motif</i>}</div>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function DeletedAgents({ items, search, isAdmin, onChanged }) {
  const q = search.trim().toLowerCase();
  const rows = items.filter((t) => !q || [t.first_name, t.last_name, t.email, t.phone, t.carte_pro_number].some((v) => (v || "").toLowerCase().includes(q)));
  const allow = async (t) => {
    if (!window.confirm(`Autoriser ${t.first_name} ${t.last_name} à postuler de nouveau ?`)) return;
    try { await http.post(`/agents/deleted/${t.id}/allow`); onChanged(); } catch (e) { window.alert(formatApiError(e)); }
  };
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid="deleted-agents-table">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-600">Agents supprimés : ils ne peuvent plus postuler sur la page /postuler, sauf autorisation d'un administrateur.</div>
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200">
          <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-slate-500">
            <th className="px-4 py-3">Agent</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Téléphone</th><th className="px-4 py-3">Carte pro</th><th className="px-4 py-3">Supprimé</th><th className="px-4 py-3 text-right">Candidature</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((t) => (
            <tr key={t.id} data-testid={`deleted-agent-${t.id}`}>
              <td className="px-4 py-3 font-semibold">{t.last_name} <span className="text-slate-500 font-normal">{t.first_name}</span></td>
              <td className="px-4 py-3">{t.email || "—"}</td>
              <td className="px-4 py-3">{t.phone || "—"}</td>
              <td className="px-4 py-3 font-mono text-xs">{t.carte_pro_number || "—"}</td>
              <td className="px-4 py-3 text-xs text-slate-600">{t.deleted_at ? new Date(t.deleted_at).toLocaleDateString("fr-FR") : "—"}{t.deleted_by && <> · par {t.deleted_by}</>}</td>
              <td className="px-4 py-3 text-right">
                {t.allowed
                  ? <span className="text-xs font-semibold text-emerald-700" title={`Autorisé le ${t.allowed_at ? new Date(t.allowed_at).toLocaleDateString("fr-FR") : ""} par ${t.allowed_by || ""}`}>Autorisé à postuler</span>
                  : isAdmin
                    ? <button data-testid={`allow-deleted-${t.id}`} onClick={() => allow(t)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50"><ShieldOff className="h-3.5 w-3.5" /> Autoriser à postuler</button>
                    : <span className="text-xs font-semibold text-rose-700">Bloqué</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-10 text-sm">Aucun agent supprimé.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
